import {useCallback,useEffect,useRef,useState} from 'react';

export default function useDailyDuties(api,user,enabled) {
 const [state,setState]=useState({loading:false,data:null,error:null});
 const [retry,setRetry]=useState(0);
 const scope=`${user?.user_id||''}|${user?.role||''}|${user?.boutique_code||''}|${enabled}`;
 const live=useRef(scope);live.current=scope;
 const pending=useRef(null);
 const generation=useRef(0);
 const writing=useRef(false);
 useEffect(()=>{
  pending.current=null;generation.current+=1;
  if(!enabled){setState({loading:false,data:null,error:null});return;}
  let active=true,running=false;
  setState({loading:true,data:null,error:null});
  const refresh=async(force=false)=>{
   if(running || (!force && document.visibilityState==='hidden'))return;
   running=true;const version=generation.current;
   try{
    const context=await api.get('/me/day/context');
    if(context.data?.ambiguous || !context.data?.boutique_code)throw new Error('Your boutique assignment needs clarification in Room Assignments.');
    const result=await api.get('/me/duties',{params:{boutique:context.data.boutique_code}});
    if(!Array.isArray(result.data?.tasks) || !Array.isArray(result.data?.history) || !result.data?.date)throw new Error('Your duties could not be checked.');
    if(active && version===generation.current)setState({loading:false,data:result.data,error:null});
   }catch(e){if(active && version===generation.current)setState({loading:false,data:null,error:e.response?.data?.detail || e.message || 'Your duties could not be checked.'});}
   finally{running=false;}
  };
  refresh(true);const onVisible=()=>refresh();const timer=window.setInterval(onVisible,60000);
  window.addEventListener('focus',onVisible);document.addEventListener('visibilitychange',onVisible);
  return()=>{active=false;window.clearInterval(timer);window.removeEventListener('focus',onVisible);document.removeEventListener('visibilitychange',onVisible);};
 },[api,scope,retry,enabled]);
 const mutate=useCallback(async(kind,value)=>{
  const startingScope=scope;
  if(writing.current)throw new Error('Wait for your current change to finish saving.');
  const data=state.data;
  if(!data)throw new Error('Reload your duties before making changes.');
  const task=data.tasks.find(t=>kind==='handoff-issue' ? t.room===value.room : t.id===value.task_id);
  if(!task)throw new Error('This duty is not currently assigned to you.');
  const payload={...value,date:data.date,revision:task.revision};
  const fingerprint=JSON.stringify({kind,...payload});
  // Retain the request key after transport failure so a retry cannot duplicate a round/audit entry.
  const request=pending.current?.fingerprint===fingerprint?pending.current.request:{...payload,request_id:crypto.randomUUID()};
  pending.current={fingerprint,request};
  writing.current=true;generation.current+=1;
  try{
   const result=await api.post(`/me/duties/${kind}`,request,{params:{boutique:data.boutique_code}});
   if(live.current===startingScope){pending.current=null;setState({loading:false,data:result.data,error:null});}
   return result.data;
  }catch(e){
   if(e.response?.status===409 || e.response?.status===403){pending.current=null;setRetry(r=>r+1);}
   throw e;
  }finally{writing.current=false;if(live.current===startingScope)generation.current+=1;}
 },[api,scope,state.data]);
 const respond=async(notice,value)=>{
  const startingScope=scope;
  const fingerprint=JSON.stringify({notice:notice.id,...value,revision:notice.revision});
  const request=pending.current?.fingerprint===fingerprint?pending.current.request:{...value,revision:notice.revision,request_id:crypto.randomUUID()};
  pending.current={fingerprint,request};
  const result=await api.post(`/me/duties/issues/${notice.id}/response`,request);
  if(live.current===startingScope){pending.current=null;setState(current=>({...current,data:{...current.data,notices:current.data.notices.map(n=>n.id===notice.id?result.data:n)}}));}
 };
 return {...state,onIssue:value=>mutate('handoff-issue',value),onIssueResponse:respond,onRetry:()=>setRetry(r=>r+1),onItem:value=>mutate('item',value),onComplete:value=>mutate('complete',value),onNewRound:task_id=>mutate('round',{task_id}),onNote:value=>mutate('note',value)};
}
